import { ZONA_HORARIA, esCuentaEnDolares, formatMoney, hoy, type Centavos, type Settings } from '@abastece/shared';
import type { Deposito, MovimientoDueno, Recarga, TransferenciaBanco } from '@abastece/core';
import { conCuentasPuestas, nombreDeCuenta } from '../lib/bancos.js';
import { etiquetaRango } from '../lib/historial.js';
import type { PagoHecho, Turno } from '../lib/historialDinero.js';
import { dolaresDeLosLibros } from '../lib/libros.js';
import { localeActual, useT } from '../i18n/index.js';
import { enDolares } from './CuentasDeCaja.js';
import { Ico } from './Iconos.js';

/**
 * A row of each money history, said the same on the screen that keeps the latest few and on the
 * one with all of it (`lib/historialDinero.ts`). On the whole one a row says its day, with the
 * year once it is not this one's, and how it went: the few latest go without, being recent.
 */

/** A day on a list that goes back years: «6 oct», «22 ago 2025». */
export const diaDeLista = (fecha: string): string => etiquetaRango(fecha, '', hoy(), localeActual());

/** A second line under a row, when there is one: whole, wrapping, since it is what tells two rows apart. */
function Detalle({ texto }: { texto: string | undefined }) {
  return texto ? <span className="mt-0.5 block text-xs wrap-break-word text-slate-500">{texto}</span> : null;
}

/**
 * The row's first line: cut short on a screen's few latest, where it is one line in a card among
 * others; whole on the history itself, where what a payment was for is what somebody came to find.
 */
const primeraLinea = (entera: boolean) => (entera ? 'wrap-break-word' : 'truncate');

/** «Anular», small, at the end of a row. */
function BotonAnular({ onClick, ...datos }: { onClick: () => void } & Record<`data-${string}`, string>) {
  const t = useT();
  return (
    <button type="button" className="btn-texto gap-1 text-xs text-slate-400" onClick={onClick} {...datos}>
      <Ico icono="anular" className="size-3.5" />
      {t('comun.anular')}
    </button>
  );
}

/** A payment to a supplier. `onAnular` only where it can be voided by whoever is looking. */
export function FilaPagoProveedor({
  pago: p,
  nombre,
  detalle,
  onAnular,
  dinero,
  className = '',
}: {
  pago: PagoHecho;
  /** Whom it was paid to. */
  nombre: string;
  detalle?: string;
  onAnular?: () => void;
  /** The page's dollars (`useMonedaDeLosLibros`), its symbol said; absent, córdobas bare as ever. */
  dinero?: (c: Centavos) => string;
  className?: string;
}) {
  const t = useT();
  return (
    <li className={`flex items-center justify-between gap-3 ${className}`} data-pago-proveedor={p.id}>
      <span className="min-w-0">
        <span className={`block ${primeraLinea(detalle !== undefined)} ${p.anulado ? 'text-slate-500 line-through' : 'text-slate-300'}`}>
          {nombre} ·{' '}
          {p.conLaCompra
            ? t('compras.conLaCompra', { fecha: diaDeLista(p.fecha) })
            : p.conElGasto
              ? t('gastos.conElGasto', { fecha: diaDeLista(p.fecha) })
              : diaDeLista(p.fecha)}
        </span>
        <Detalle texto={detalle} />
      </span>
      <span className="flex shrink-0 items-center gap-3">
        <span className={`tabular ${p.anulado ? 'text-slate-500 line-through' : ''}`}>{dinero ? dinero(p.monto) : formatMoney(p.monto, { symbol: false })}</span>
        {p.anulado ? (
          <span className="text-xs text-rose-400">{t('gastos.anulado')}</span>
        ) : (
          onAnular && <BotonAnular onClick={onAnular} data-gastos-anular-pago="" />
        )}
      </span>
    </li>
  );
}

/** Money the owner took out or put in. */
export function FilaMovimientoDueno({
  movimiento: mv,
  detalle,
  onAnular,
  dinero,
  className = '',
}: {
  movimiento: MovimientoDueno;
  detalle?: string;
  onAnular?: () => void;
  /**
   * The page's dollars (`useMonedaDeLosLibros`), its symbol said; absent, córdobas bare as ever. Out
   * of or into a dollar account it says that account's own dollars, what its statement shows.
   */
  dinero?: (c: Centavos) => string;
  className?: string;
}) {
  const t = useT();
  return (
    <li className={`flex items-center justify-between gap-3 ${className}`} data-movimiento-dueno={mv.id}>
      <span className="min-w-0">
        <span className={`block ${primeraLinea(detalle !== undefined)} ${mv.anulado ? 'text-slate-500 line-through' : 'text-slate-300'}`}>
          {t(mv.tipo === 'retiro' ? 'gastos.retiro' : 'gastos.aporte')}
          {mv.motivo ? ` — ${mv.motivo}` : ''}
        </span>
        <Detalle texto={detalle} />
      </span>
      <span className="flex shrink-0 items-center gap-3">
        <span
          className={`tabular ${mv.anulado ? 'text-slate-500 line-through' : mv.tipo === 'retiro' ? 'text-amber-300' : 'text-teal-300'}`}
        >
          {mv.tipo === 'retiro' ? '−' : '+'}
          {!dinero ? formatMoney(mv.monto, { symbol: false }) : (mv.dolares ?? 0) > 0 ? dolaresDeLosLibros(mv.dolares!) : dinero(mv.monto)}
          {/* Out of or into a dollar account: its dollars, which is what the statement shows. */}
          {!dinero && (mv.dolares ?? 0) > 0 && <span className="block text-right text-xs text-slate-400">{enDolares(mv.dolares!)}</span>}
        </span>
        {mv.anulado ? (
          <span className="text-xs text-rose-400">{t('gastos.anulado')}</span>
        ) : (
          onAnular && <BotonAnular onClick={onAnular} data-gastos-anular-dueno="" />
        )}
      </span>
    </li>
  );
}

/** Cash taken to the bank: the day, the slip, each currency in it. */
export function FilaDeposito({ deposito: d, config, onAnular }: { deposito: Deposito; config?: Settings; onAnular?: () => void }) {
  const t = useT();
  // Which account it went to or came from, once the owner has set them up (`cuentasBanco`).
  const cuentas =
    config && conCuentasPuestas(config)
      ? [
          d.cordobas > 0 ? nombreDeCuenta(config, d.cuenta ?? 'BANCOS', t) : null,
          d.dolares > 0 ? nombreDeCuenta(config, d.cuentaDolares ?? 'BANCO_DOLARES', t) : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : null;
  return (
    <li className="flex items-center justify-between gap-3 py-2 text-sm" data-deposito={d.id}>
      <span className="min-w-0">
        <span className={`block ${d.anulado ? 'text-slate-500 line-through' : ''}`}>
          {diaDeLista(d.fecha)}
          {d.referencia ? ` · ${t('anular.boleta', { referencia: d.referencia })}` : ''}
        </span>
        {cuentas && (
          <span className="block text-xs wrap-break-word text-slate-400" data-deposito-cuentas>
            {cuentas}
          </span>
        )}
        {d.anulado ? (
          <span className="block text-xs text-rose-400">{t('gastos.anulado')}</span>
        ) : (
          <Detalle texto={d.nota ?? undefined} />
        )}
      </span>
      <span className="flex shrink-0 items-center gap-3">
        <span className={`tabular text-right ${d.anulado ? 'text-slate-500 line-through' : ''}`}>
          {[d.cordobas > 0 ? formatMoney(d.cordobas) : null, d.dolares > 0 ? enDolares(d.dolares) : null].filter(Boolean).join(' · ')}
        </span>
        {!d.anulado && onAnular && <BotonAnular onClick={onAnular} data-efectivo-anular-deposito="" />}
      </span>
    </li>
  );
}

/**
 * Money from one bank account to another: from where to where, what left, and what arrived when it
 * changed currency on the way.
 */
export function FilaTransferenciaBanco({
  transferencia: x,
  config,
  onAnular,
}: {
  transferencia: TransferenciaBanco;
  config: Settings;
  onAnular?: () => void;
}) {
  const t = useT();
  const enSuMoneda = (cuenta: string, c: Centavos) => (esCuentaEnDolares(cuenta) ? enDolares(c) : formatMoney(c));
  const cambia = esCuentaEnDolares(x.desde) !== esCuentaEnDolares(x.hacia);
  return (
    <li className="flex items-center justify-between gap-3 py-2 text-sm" data-transferencia-banco={x.id}>
      <span className="min-w-0">
        <span className={`block wrap-break-word ${x.anulado ? 'text-slate-500 line-through' : ''}`}>
          {t('bancos.deA', { desde: nombreDeCuenta(config, x.desde, t), hacia: nombreDeCuenta(config, x.hacia, t) })}
        </span>
        <span className="block text-xs text-slate-500">
          {diaDeLista(x.fecha)}
          {x.referencia ? ` · ${x.referencia}` : ''}
        </span>
        {x.anulado ? <span className="block text-xs text-rose-400">{t('gastos.anulado')}</span> : <Detalle texto={x.nota ?? undefined} />}
      </span>
      <span className="flex shrink-0 items-center gap-3">
        <span className={`tabular text-right ${x.anulado ? 'text-slate-500 line-through' : ''}`}>
          {enSuMoneda(x.desde, x.monto)}
          {cambia && <span className="block text-xs text-slate-400">→ {enSuMoneda(x.hacia, x.recibido)}</span>}
        </span>
        {!x.anulado && onAnular && <BotonAnular onClick={onAnular} data-efectivo-anular-transferencia="" />}
      </span>
    </li>
  );
}

/** Airtime sold: the carrier, the number, the amount and what it earned. */
export function FilaRecarga({ recarga: r, detalle }: { recarga: Recarga; detalle?: string }) {
  const t = useT();
  return (
    <li className="flex items-baseline justify-between gap-3 py-2 text-sm" data-recarga={r.id}>
      <span className="min-w-0">
        <span className="block">
          {r.operadora} <span className="tabular text-slate-500">{r.telefono}</span>
          {r.estadoPago === 'pendiente' && <span className="ml-2 text-xs text-amber-300">{t('recargas.porConfirmar')}</span>}
          {r.estadoPago === 'rechazado' && <span className="ml-2 text-xs text-rose-300">{t('recargas.noLlego')}</span>}
        </span>
        <Detalle texto={detalle} />
      </span>
      <span className="tabular shrink-0">
        {formatMoney(r.montoFacial, { symbol: false })}
        {r.comision > 0 && <span className="ml-2 text-xs text-emerald-400">+{formatMoney(r.comision, { symbol: false })}</span>}
      </span>
    </li>
  );
}

/** A till's shift as «Turnos anteriores» shows it. */
export type TurnoCerrado = Turno & {
  entregado?: Centavos | null;
  entregadoDolares?: Centavos | null;
  primerConteoApertura?: Centavos | null;
  primerConteoCordobas?: Centavos | null;
  primerConteoDolares?: Centavos | null;
  primerConteoEntregaCordobas?: Centavos | null;
  primerConteoEntregaDolares?: Centavos | null;
};

/**
 * A shift gone by: its day, what it sold and handed over, whose it was, and what its counts found.
 * For the owner (`onCorregir`), its opening and its corte can be corrected from here (2026-10-14).
 */
export function TarjetaTurno({
  sesion: s,
  nombre,
  onCorregir,
}: {
  sesion: TurnoCerrado;
  nombre: (id: string) => string;
  onCorregir?: (cual: 'apertura' | 'corte') => void;
}) {
  const t = useT();
  return (
    <li className="tarjeta flex flex-col gap-2" data-turno={s.id}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium">
            {new Date(s.abiertaEn).toLocaleDateString(localeActual(), {
              dateStyle: 'medium',
              timeZone: ZONA_HORARIA,
            })}
          </p>
          <p className="tabular mt-0.5 text-xs text-slate-500">{t('caja.vendido', { monto: formatMoney(s.totalVendido) })}</p>
          {(s.entregado ?? 0) + (s.entregadoDolares ?? 0) > 0 && (
            <p className="tabular mt-0.5 text-xs text-slate-500">
              {t('caja.entregado', {
                monto:
                  formatMoney(s.entregado ?? (0 as Centavos)) + ((s.entregadoDolares ?? 0) > 0 ? ` + ${enDolares(s.entregadoDolares!)}` : ''),
              })}
            </p>
          )}
          {/* Whose it was: a till that changed hands, or closed by somebody else, says so. */}
          {s.cerradaPor && s.cerradaPor !== s.usuarioId && (
            <p className="mt-0.5 text-xs text-slate-500">{t('caja.abrioCerro', { abrio: nombre(s.usuarioId), cerro: nombre(s.cerradaPor) })}</p>
          )}
          {(s.diferenciaEntregas ?? 0) !== 0 && (
            <p className={`tabular mt-0.5 text-xs ${s.diferenciaEntregas! < 0 ? 'text-rose-400' : 'text-amber-400'}`}>
              {t('caja.enEntregas', { monto: formatMoney(s.diferenciaEntregas!, { signed: true }) })}
            </p>
          )}
          {(s.diferenciaApertura ?? 0) !== 0 && (
            <p className={`tabular mt-0.5 text-xs ${s.diferenciaApertura! < 0 ? 'text-rose-400' : 'text-amber-400'}`}>
              {t('caja.alAbrir', { monto: formatMoney(s.diferenciaApertura!, { signed: true }) })}
            </p>
          )}
          {/* A count that was off and was redone: the owner sees the first one beside the result. */}
          {s.primerConteoApertura != null && (
            <p className="tabular mt-0.5 text-xs text-amber-400">
              {t('caja.primerConteoAlAbrir', { monto: formatMoney(s.primerConteoApertura) })}
            </p>
          )}
          {(s.primerConteoEntregaCordobas != null || s.primerConteoEntregaDolares != null) && (
            <p className="tabular mt-0.5 text-xs text-amber-400">
              {t('caja.primerConteoAlRecibir', {
                monto: [
                  s.primerConteoEntregaCordobas != null ? formatMoney(s.primerConteoEntregaCordobas) : null,
                  s.primerConteoEntregaDolares != null ? enDolares(s.primerConteoEntregaDolares) : null,
                ]
                  .filter(Boolean)
                  .join(' + '),
              })}
            </p>
          )}
          {(s.primerConteoCordobas != null || s.primerConteoDolares != null) && (
            <p className="tabular mt-0.5 text-xs text-amber-400">
              {t('caja.primerConteoAlCerrar', {
                monto: [
                  s.primerConteoCordobas != null ? formatMoney(s.primerConteoCordobas) : null,
                  s.primerConteoDolares != null ? enDolares(s.primerConteoDolares) : null,
                ]
                  .filter(Boolean)
                  .join(' + '),
              })}
            </p>
          )}
        </div>
        <span className="shrink-0 text-right">
          <span
            className={`tabular block text-sm ${
              s.diferenciaCordobas === 0 ? 'text-emerald-400' : (s.diferenciaCordobas ?? 0) < 0 ? 'text-rose-400' : 'text-amber-400'
            }`}
          >
            {s.diferenciaCordobas === 0 ? t('caja.cuadro') : formatMoney((s.diferenciaCordobas ?? 0) as Centavos, { signed: true })}
          </span>
          {/* Only when the dollars were off: a shift that matched in both says «cuadró» once. */}
          {(s.diferenciaDolares ?? 0) !== 0 && (
            <span className={`tabular mt-0.5 block text-xs ${s.diferenciaDolares! < 0 ? 'text-rose-400' : 'text-amber-400'}`}>
              {enDolares(s.diferenciaDolares!, true)}
            </span>
          )}
        </span>
      </div>
      {onCorregir && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-slate-800 pt-2">
          <button type="button" className="btn-texto gap-1 text-xs text-slate-300" data-turno-corregir="apertura" onClick={() => onCorregir('apertura')}>
            <Ico icono="editar" className="size-3.5" />
            {t('conteoCaja.tituloApertura')}
          </button>
          <button type="button" className="btn-texto gap-1 text-xs text-slate-300" data-turno-corregir="corte" onClick={() => onCorregir('corte')}>
            <Ico icono="editar" className="size-3.5" />
            {t('conteoCaja.tituloCorte')}
          </button>
        </div>
      )}
    </li>
  );
}
